// Thin fetch wrapper for the BFF.
//
// - 自动从 localStorage 读 token 注入 Authorization 头
// - 401 时清 token + 让 caller 决定跳转（在 layout 里 useEffect 监听）
// - JSON 响应自动 parse；body 非 JSON 时回 raw text
// - 错误以 ApiError 抛出（含 status + code + message），UI 显示 message
import { env } from '../env/env';

const TOKEN_KEY = 'jiali.admin.token';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

function readToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(t: string | null) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, headers, ...rest } = init;
  const h = new Headers(headers);
  const tok = readToken();
  if (tok) h.set('Authorization', `Bearer ${tok}`);
  if (json !== undefined) {
    h.set('Content-Type', 'application/json');
  }
  const url = `${env.apiBaseUrl}${path}`;
  const resp = await fetch(url, {
    ...rest,
    headers: h,
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  // 204 No Content
  if (resp.status === 204) return undefined as T;
  const text = await resp.text();
  let parsed: unknown = text;
  try {
    parsed = text ? JSON.parse(text) : undefined;
  } catch {
    // 非 JSON 响应保留 text
  }
  if (!resp.ok) {
    const body = parsed as { code?: string; message?: string } | string;
    const code =
      typeof body === 'object' && body?.code
        ? body.code
        : `HTTP.${resp.status}`;
    const message =
      typeof body === 'object' && body?.message
        ? body.message
        : typeof body === 'string' && body
          ? body
          : resp.statusText;
    if (resp.status === 401) {
      setToken(null);
    }
    throw new ApiError(resp.status, code, message);
  }
  return parsed as T;
}
